import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  X,
  User,
  BookOpen,
  BarChart3,
  Heart,
  ClipboardList,
  CreditCard,
  FileText,
  Pencil,
  PowerOff,
  RotateCcw,
  Download,
  ArrowRightLeft,
  Check,
} from 'lucide-react';
import { api } from '../../api/client';
import {
  formatPresencaDate,
  presencaDayNumber,
  useFrequenciaAluno,
} from '../../api/hooks/usePresencasDiarias';
import type { Aluno } from '../../types/models';
import {
  AcademicoTab,
  BoletimTab,
  ResponsavelTab,
} from './AlunoFicha';
import './responsavel-ficha-desktop.css';
import { ROUTES } from '../../api/routes';
import { ALUNOS } from '../../api/routes';
import { JustificativasAlunoPanel } from './JustificativasAlunoPanel';

const SITUACAO: Record<string, { label: string; color: string }> = {
  ATIVO: { label: 'Ativo', color: '#047857' },
  TRANSFERIDO: { label: 'Transferido', color: '#b45309' },
  EVADIDO: { label: 'Evadido', color: '#dc2626' },
  FORMADO: { label: 'Formado', color: '#1d4ed8' },
  DESISTENTE: { label: 'Desistente', color: '#7c3aed' },
  FALECIDO: { label: 'Falecido', color: '#64748b' },
};

const TABS = [
  { key: 'visao', label: 'Visão escola', icon: BarChart3 },
  { key: 'dados', label: 'Dados pessoais', icon: User },
  { key: 'responsavel', label: 'Responsável', icon: Heart },
  { key: 'academico', label: 'Acadêmico', icon: BookOpen },
  { key: 'boletim', label: 'Boletim', icon: ClipboardList },
  { key: 'rfid', label: 'RFID / Acesso', icon: CreditCard },
  { key: 'frequencia', label: 'Frequência', icon: BarChart3 },
] as const;

type TabKey = (typeof TABS)[number]['key'];

function hasFoto(fotoUrl?: string | null) {
  if (!fotoUrl) return false;
  const u = fotoUrl.toLowerCase();
  return !u.includes('i.pravatar.cc') && !u.includes('ui-avatars.com');
}

function fmtDate(d?: string) {
  return formatPresencaDate(d);
}

function DlCard({
  title,
  icon: Icon,
  iconColor,
  rows,
}: {
  title: string;
  icon: typeof User;
  iconColor?: string;
  rows: { dt: string; dd: string }[];
}) {
  return (
    <div className="resp-ficha-card resp-ficha-card--contato">
      <h3 className="resp-ficha-card-title">
        <Icon size={12} color={iconColor} /> {title}
      </h3>
      <dl className="resp-ficha-dl resp-ficha-dl--contato aluno-ficha-dl--compact">
        {rows.map((r) => (
          <div key={r.dt}>
            <dt>{r.dt}</dt>
            <dd>{r.dd}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

export function AlunoFichaDesktop({
  alunoId,
  escolaId,
  canManage,
  onClose,
}: {
  alunoId: string;
  escolaId: string;
  canManage: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<TabKey>('visao');
  const queryClient = useQueryClient();

  const { data: aluno, isLoading } = useQuery({
    queryKey: ['aluno-ficha', escolaId, alunoId],
    queryFn: async () => {
      const { data } = await api.get<Aluno>(ALUNOS.findOne(escolaId, alunoId));
      return data;
    },
    enabled: !!escolaId && !!alunoId,
  });

  const { frequencia } = useFrequenciaAluno(alunoId);
  const pct = frequencia.percentual;
  const temFreq = frequencia.totalDias > 0;
  const semaforoColor = !temFreq
    ? '#94a3b8'
    : pct >= 85
      ? '#047857'
      : pct >= 75
        ? '#b45309'
        : '#dc2626';
  const situacao = SITUACAO[aluno?.situacao || 'ATIVO'] || SITUACAO.ATIVO;

  const qtdResp = aluno?.responsaveis?.length ?? 0;
  const turmaNome = aluno?.turma?.nome || 'Sem turma';

  const chartPresencas = useMemo(() => {
    const map = new Map<string, number>();
    frequencia.presencas.slice(0, 14).forEach((p) => {
      const label = formatPresencaDate(p.data);
      if (label === '—') return;
      if (p.status === 'PRESENTE' || p.status === 'ATRASADO' || p.status === 'PARCIAL') {
        map.set(label, (map.get(label) ?? 0) + 1);
      }
    });
    return [...map.entries()].map(([dia, presentes]) => ({ dia, presentes }));
  }, [frequencia.presencas]);

  if (isLoading || !aluno) {
    return (
      <div className="resp-ficha aluno-ficha-page aluno-ficha-page--modal">
        <div className="resp-ficha-empty">Carregando ficha…</div>
      </div>
    );
  }

  const tags = aluno.tags ?? aluno.tagsRfid ?? [];

  return (
    <div className="resp-ficha aluno-ficha-page aluno-ficha-page--modal">
      <header className="resp-ficha-head">
        {hasFoto(aluno.fotoUrl) ? (
          <img src={aluno.fotoUrl!} alt="" className="resp-ficha-avatar" />
        ) : (
          <span className="resp-ficha-avatar-ph">
            <User size={32} />
          </span>
        )}
        <div className="resp-ficha-head-main">
          <p className="resp-ficha-eyebrow">Ficha do Aluno</p>
          <h2 className="resp-ficha-name">{aluno.nome}</h2>
          <div className="resp-ficha-meta-line">
            <span className="resp-ficha-sub">
              {aluno.matricula || 'Sem matrícula'}
              {turmaNome ? ` · ${turmaNome}` : ''}
            </span>
            <span
              className={`resp-ficha-badge ${aluno.ativo ? 'resp-ficha-badge--ok' : 'resp-ficha-badge--off'}`}
            >
              {aluno.ativo ? 'Cadastro ativo' : 'Inativo'}
            </span>
            <span
              className="resp-ficha-badge"
              style={{
                color: situacao.color,
                borderColor: `${situacao.color}55`,
                background: `${situacao.color}12`,
              }}
            >
              {situacao.label}
            </span>
          </div>
        </div>
        <button type="button" className="resp-ficha-close" onClick={onClose} aria-label="Fechar">
          <X size={16} />
        </button>
      </header>

      <div className="resp-ficha-body">
        <div className="resp-ficha-stack">
          <nav className="resp-ficha-tabs resp-ficha-tabs--scroll" aria-label="Secções da ficha">
            {TABS.map((t) => {
              const Icon = t.icon;
              return (
                <button
                  key={t.key}
                  type="button"
                  className={`resp-ficha-tab${tab === t.key ? ' resp-ficha-tab--active' : ''}`}
                  onClick={() => setTab(t.key)}
                >
                  <Icon size={12} />
                  {t.label}
                  {t.key === 'responsavel' && qtdResp > 0 && (
                    <span className="resp-ficha-tab-count">({qtdResp})</span>
                  )}
                </button>
              );
            })}
          </nav>

          <div className="resp-ficha-panel">
            {tab === 'visao' && (
              <div className="resp-ficha-grid2">
                <div className="resp-ficha-card">
                  <h3 className="resp-ficha-card-title">
                    <BarChart3 size={12} /> Frequência na escola
                  </h3>
                  <div className="resp-ficha-card-body resp-ficha-card-body--insights">
                    <div className="resp-ficha-insights">
                      <p className="resp-ficha-insight">
                        <strong style={{ color: semaforoColor }}>
                          {temFreq ? `${pct}%` : '—'}
                        </strong>{' '}
                        {temFreq
                          ? 'de frequência nos registros do período.'
                          : 'Sem registros de presença no período.'}
                      </p>
                      <p className="resp-ficha-insight">
                        {temFreq
                          ? `${frequencia.diasPresente} presença(s) em ${frequencia.totalDias} dia(s) letivo(s) contabilizados.`
                          : 'Nenhum dia letivo consolidado ainda para este aluno.'}
                      </p>
                      <p className="resp-ficha-insight">
                        {!temFreq
                          ? 'Aguarde chamadas ou consolidação do dia para acompanhar a frequência.'
                          : frequencia.emRisco
                            ? 'Atenção LDB: frequência abaixo de 75% — acompanhamento pedagógico recomendado.'
                            : pct >= 85
                              ? 'Frequência regular — dentro do esperado para a unidade.'
                              : 'Frequência em atenção — monitore ausências nas próximas semanas.'}
                      </p>
                      {chartPresencas.length > 0 && (
                        <p className="resp-ficha-insight">
                          Últimos registros positivos em {chartPresencas.length} dia(s) recente(s).
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="resp-ficha-card">
                  <h3 className="resp-ficha-card-title">
                    <BookOpen size={12} /> Situação acadêmica
                  </h3>
                  <div className="resp-ficha-card-body resp-ficha-card-body--insights">
                    <div className="resp-ficha-insights">
                      <p className="resp-ficha-insight">
                        Matrícula <strong>{aluno.matricula || '—'}</strong> na turma{' '}
                        <strong>{turmaNome}</strong>
                        {aluno.turma?.serie ? ` (${aluno.turma.serie})` : ''}.
                      </p>
                      <p className="resp-ficha-insight">
                        Turno: <strong>{aluno.turma?.turnoOperacional?.nome || aluno.turma?.turno || '—'}</strong>
                        {aluno.anoIngresso ? ` · Ingresso ${aluno.anoIngresso}` : ''}.
                      </p>
                      <p className="resp-ficha-insight">
                        Situação cadastral: <strong>{situacao.label}</strong>
                        {aluno.consentimentoLGPD ? ' · LGPD consentido' : ' · LGPD pendente'}.
                      </p>
                      <p className="resp-ficha-insight">
                        {qtdResp > 0
                          ? `${qtdResp} responsável(is) vinculado(s) — ver aba Responsável para contatos.`
                          : 'Nenhum responsável vinculado — complete o cadastro familiar.'}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {tab === 'dados' && (
              <div className="aluno-ficha-dados-grid">
                <DlCard
                  title="Informações pessoais"
                  icon={User}
                  rows={[
                    { dt: 'Nome', dd: aluno.nome },
                    { dt: 'Nascimento', dd: fmtDate(aluno.dataNascimento) },
                    { dt: 'Gênero', dd: aluno.genero || '—' },
                    { dt: 'CPF', dd: aluno.cpf || '—' },
                    { dt: 'Naturalidade', dd: aluno.naturalidade || '—' },
                    { dt: 'Nacionalidade', dd: aluno.nacionalidade?.replace(/_/g, ' ') || '—' },
                    { dt: 'País de origem', dd: aluno.paisOrigem || '—' },
                    { dt: 'Cor/Raça', dd: aluno.corRaca?.replace(/_/g, ' ') || '—' },
                  ]}
                />
                <DlCard
                  title="Saúde & NEE"
                  icon={Heart}
                  rows={[
                    { dt: 'Sangue', dd: aluno.tipoSanguineo || '—' },
                    { dt: 'Alergias', dd: aluno.alergias || 'Nenhuma' },
                    { dt: 'Restrição', dd: aluno.restricaoAlimentar || 'Nenhuma' },
                    { dt: 'Plano', dd: aluno.planoSaude || '—' },
                    { dt: 'Deficiência', dd: aluno.tipoDeficiencia?.replace(/_/g, ' ') || 'Nenhuma' },
                    { dt: 'Detalhe NEE', dd: aluno.deficienciaDetalhe || '—' },
                  ]}
                />
                <div className="resp-ficha-card resp-ficha-card--contato aluno-ficha-card--wide">
                  <h3 className="resp-ficha-card-title">
                    <FileText size={12} /> Endereço & LGPD
                  </h3>
                  <dl className="resp-ficha-dl resp-ficha-dl--contato aluno-ficha-dl--endereco">
                    <div><dt>CEP</dt><dd>{aluno.cep || '—'}</dd></div>
                    <div><dt>Cidade/UF</dt><dd>{aluno.cidade ? `${aluno.cidade}/${aluno.uf || ''}` : '—'}</dd></div>
                    <div><dt>Bairro</dt><dd>{aluno.bairro || '—'}</dd></div>
                    <div><dt>Número</dt><dd>{aluno.numero || '—'}</dd></div>
                    <div><dt>Complemento</dt><dd>{aluno.complemento || '—'}</dd></div>
                    <div className="resp-ficha-dl-span"><dt>Endereço</dt><dd>{aluno.endereco || '—'}</dd></div>
                    <div>
                      <dt>Geolocalização</dt>
                      <dd>
                        {aluno.latitude != null && aluno.longitude != null
                          ? `${Number(aluno.latitude).toFixed(5)}, ${Number(aluno.longitude).toFixed(5)}`
                          : '—'}
                      </dd>
                    </div>
                    <div>
                      <dt>LGPD</dt>
                      <dd>
                        {aluno.consentimentoLGPD
                          ? `OK${aluno.dataConsentimento ? ` · ${fmtDate(aluno.dataConsentimento)}` : ''}${aluno.ipConsentimento ? ` · IP ${aluno.ipConsentimento}` : ''}`
                          : 'Pendente'}
                      </dd>
                    </div>
                    <div className="resp-ficha-dl-span">
                      <dt>Auditoria</dt>
                      <dd>
                        Criado {fmtDate(aluno.criadoEm)}
                        {aluno.criadoPor?.nome ? ` · ${aluno.criadoPor.nome}` : ''}
                        {' · '}
                        Atualizado {fmtDate(aluno.atualizadoEm)}
                        {aluno.atualizadoPor?.nome ? ` · ${aluno.atualizadoPor.nome}` : ''}
                      </dd>
                    </div>
                  </dl>
                  <div className="resp-ficha-lgpd-row">
                    <span className={`resp-ficha-lgpd-badge${aluno.consentimentoLGPD ? ' resp-ficha-lgpd-badge--ok' : ''}`}>
                      {aluno.consentimentoLGPD ? <><Check size={10} /> LGPD OK</> : 'LGPD pendente'}
                    </span>
                    {canManage && (
                      <button
                        type="button"
                        className="resp-ficha-lgpd-btn"
                        onClick={async () => {
                          try {
                            const res = await api.get(ROUTES.alunos.findOne(escolaId, alunoId) + '/pdf/termo-lgpd', { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `termo-lgpd-${aluno.nome}.pdf`;
                            a.click();
                            window.URL.revokeObjectURL(url);
                          } catch {
                            /* toast no pai se necessário */
                          }
                        }}
                      >
                        <Download size={10} /> Termo
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {tab === 'responsavel' && (
              <div className="aluno-ficha-tab-pad">
                <ResponsavelTab
                  aluno={aluno}
                  escolaId={escolaId}
                  queryClient={queryClient}
                  canManage={canManage}
                />
              </div>
            )}

            {tab === 'academico' && (
              <div className="aluno-ficha-tab-pad">
                <AcademicoTab
                  aluno={aluno}
                  escolaId={escolaId}
                  queryClient={queryClient}
                  canManage={canManage}
                />
              </div>
            )}

            {tab === 'boletim' && (
              <div className="aluno-ficha-tab-pad">
                <BoletimTab escolaId={escolaId} alunoId={alunoId} />
              </div>
            )}

            {tab === 'rfid' && (
              <div className="aluno-ficha-tab-pad">
                <div className="resp-ficha-card resp-ficha-card--full resp-ficha-card--contato">
                  {tags.length > 0 ? (
                    <ul className="aluno-ficha-list">
                      {tags.map((tag: { id: string; codigo: string; descricao?: string; ativa?: boolean }) => (
                        <li key={tag.id} className="aluno-ficha-list-item">
                          <CreditCard size={16} className="aluno-ficha-list-icon" />
                          <div className="aluno-ficha-list-main">
                            <span className="aluno-ficha-list-title aluno-ficha-mono">{tag.codigo}</span>
                            <span className="aluno-ficha-list-meta">{tag.descricao || 'Cartão de acesso'}</span>
                          </div>
                          <span className={`resp-ficha-tag ${tag.ativa ? 'resp-ficha-tag--notif' : 'resp-ficha-tag--off'}`}>
                            {tag.ativa ? 'Ativa' : 'Inativa'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <div className="resp-ficha-empty">
                      <CreditCard size={28} className="resp-ficha-empty-icon" />
                      <p>Nenhuma tag RFID vinculada.</p>
                    </div>
                  )}
                </div>
              </div>
            )}

            {tab === 'frequencia' && (
              <div className="aluno-ficha-tab-pad aluno-ficha-freq">
                <div className="aluno-ficha-acad-grid">
                  <div className="resp-ficha-card resp-ficha-card--contato">
                    <div className="aluno-ficha-freq-summary">
                      <div className="aluno-ficha-freq-ring" aria-hidden>
                        <svg width={88} height={88} viewBox="0 0 36 36">
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke="#e2e8f0"
                            strokeWidth="2.8"
                          />
                          <path
                            d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831"
                            fill="none"
                            stroke={semaforoColor}
                            strokeWidth="2.8"
                            strokeDasharray={`${temFreq ? pct : 0}, 100`}
                            strokeLinecap="round"
                          />
                        </svg>
                        <span style={{ color: semaforoColor }}>{temFreq ? `${pct}%` : '—'}</span>
                      </div>
                      <div>
                        <p className="aluno-ficha-freq-label">
                          {!temFreq
                            ? 'Sem registros de presença'
                            : pct >= 85
                              ? 'Frequência regular'
                              : pct >= 75
                                ? 'Atenção — frequência abaixo da média'
                                : 'ALERTA LDB — Frequência abaixo de 75%'}
                        </p>
                        <p className="aluno-ficha-freq-meta">
                          {temFreq
                            ? `${frequencia.diasPresente} dias presente de ${frequencia.totalDias} dias letivos · ${frequencia.diasAusente} faltas`
                            : 'Nenhum dia letivo consolidado ainda para este aluno.'}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="resp-ficha-card resp-ficha-card--contato">
                    <h3 className="resp-ficha-card-title">
                      <BarChart3 size={14} /> Registro de presenças
                    </h3>
                    {frequencia.presencas.length > 0 ? (
                      <>
                        <div className="aluno-ficha-freq-days">
                          {frequencia.presencas.slice(0, 42).map((p) => {
                            const tone =
                              p.status === 'PRESENTE'
                                ? 'ok'
                                : p.status === 'AUSENTE'
                                  ? 'bad'
                                  : 'warn';
                            return (
                              <span
                                key={p.id}
                                title={`${fmtDate(p.data)} — ${p.status}`}
                                className={`aluno-ficha-freq-day aluno-ficha-freq-day--${tone}`}
                              >
                                {presencaDayNumber(p.data)}
                              </span>
                            );
                          })}
                        </div>
                        <div className="aluno-ficha-freq-legend">
                          <span>Presente</span>
                          <span>Atrasado / parcial</span>
                          <span>Falta</span>
                        </div>
                      </>
                    ) : (
                      <div className="resp-ficha-empty">
                        <BarChart3 size={28} className="resp-ficha-empty-icon" />
                        <p>Nenhum registro de presença no período.</p>
                      </div>
                    )}
                  </div>
                </div>

                <JustificativasAlunoPanel escolaId={escolaId} alunoId={alunoId} />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function AlunoFichaFooter({
  aluno,
  canManage,
  onEdit,
  onTransfer,
  onDeclaracao,
  onDeactivate,
  onReactivate,
}: {
  aluno: { criadoEm: string; ativo: boolean; situacao?: string };
  canManage: boolean;
  onEdit: () => void;
  onTransfer: () => void;
  onDeclaracao: () => void;
  onDeactivate: () => void;
  onReactivate: () => void;
}) {
  return (
    <div className="resp-ficha-foot">
      <span className="resp-ficha-foot-meta">
        Cadastrado em {new Date(aluno.criadoEm).toLocaleDateString('pt-BR')}
      </span>
      <span className="resp-ficha-foot-spacer" />
      <div className="resp-ficha-foot-actions">
        {canManage && (
          <button type="button" className="resp-ficha-btn resp-ficha-btn--primary" onClick={onEdit}>
            <Pencil size={14} strokeWidth={2} />
            Editar
          </button>
        )}
        {canManage && aluno.situacao !== 'TRANSFERIDO' && (
          <button type="button" className="resp-ficha-btn" onClick={onTransfer}>
            <ArrowRightLeft size={14} strokeWidth={2} />
            Transferir
          </button>
        )}
        <button type="button" className="resp-ficha-btn" onClick={onDeclaracao}>
          <Download size={14} strokeWidth={2} />
          Declaração
        </button>
        {aluno.ativo ? (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--danger" onClick={onDeactivate}>
              <PowerOff size={14} strokeWidth={2} />
              Desativar
            </button>
          )
        ) : (
          canManage && (
            <button type="button" className="resp-ficha-btn resp-ficha-btn--success" onClick={onReactivate}>
              <RotateCcw size={14} strokeWidth={2} />
              Reativar
            </button>
          )
        )}
      </div>
    </div>
  );
}
